import { FunctionComponent, useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Auth from '@aws-amplify/auth';
import ButtonProgress from '../ButtonProgress/ButtonProgress';

const useStyles = makeStyles((theme: Theme) => ({
  warningMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

interface Props {}

const SessionTimeout: FunctionComponent<Props> = () => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const checkTokenExpiration = async () => {
      try {
        // Get current authenticated user without bypassing cache (doesn't trigger refresh)
        const user = await Auth.currentAuthenticatedUser({
          bypassCache: false,
        });
        const session = user.getSignInUserSession();

        if (session) {
          const accessToken = session.getAccessToken();
          const expirationTime = accessToken.getExpiration();
          const currentTime = Math.floor(Date.now() / 1000) + 60; //offset 1 minute
          if (expirationTime <= currentTime) {
            setIsOpen(true);
          }
        }
      } catch (error) {
        // no token, fail silent
      }
    };

    const intervalId = setInterval(checkTokenExpiration, 30000); // check every 30 seconds
    checkTokenExpiration();
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  const handleLogin = async () => {
    setIsLoading(true);
    try {
      // Force refresh the session using the refresh token
      const user = await Auth.currentAuthenticatedUser();
      const session = user.getSignInUserSession();
      const refreshToken = session.getRefreshToken();

      await new Promise<void>((resolve, reject) => {
        user.refreshSession(refreshToken, (err: any) => {
          if (err) reject(err);
          else resolve();
        });
      });
    } catch (err) {
      // If refresh fails, sign out and redirect to login
      Auth.signOut();
      localStorage.clear();
      window.location.replace('/login');
    } finally {
      setIsLoading(false);
      setIsOpen(false);
    }
  };

  const handleLogout = async () => {
    setIsLoading(true);
    Auth.signOut();
    setTimeout(() => {
      window.location.replace('/login');
    }, 1000);
  };

  return (
    <Dialog
      aria-labelledby="session-timeout-dialog-title"
      open={isOpen}
      onClose={() => {}} // Prevent closing by clicking outside
      disableEscapeKeyDown
      BackdropProps={{
        style: { backdropFilter: 'blur(5px)', WebkitBackdropFilter: 'blur(5px)' },
      }}
    >
      <DialogTitle id="session-timeout-dialog-title">
        Please sign in again
      </DialogTitle>
      <DialogContent>
        <div className={classes.warningMessage}>
          You were signed out of your account. Please press Reload to sign in
          again.
        </div>
      </DialogContent>
      <DialogActions>
        <Button disabled={isLoading} onClick={handleLogout}>
          Logout
        </Button>

        <ButtonProgress
          isLoading={isLoading}
          onClick={handleLogin}
          variant="contained"
          color="primary"
        >
          Reload
        </ButtonProgress>
      </DialogActions>
    </Dialog>
  );
};

export default SessionTimeout;
